import { Copy, Download } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { toast } from '@/components/ui/sonner'
import { schemaExportFilename, schemaToExportJson } from '@/features/schema/export'
import type { Schema } from '@/services/api/schemas'
import { downloadTextFile } from '@/utils/download'
import { SchemaHeaderSubMenu } from './SchemaHeaderActions'

/**
 * The export choices themselves, without a menu around them — so the schema
 * header can offer them from its own button and, once collapsed, from a submenu
 * of the overflow menu, without either copy owning the serialization.
 */
export function SchemaExportItems({ schema }: { schema: Schema }) {
  const { t } = useTranslation()

  const handleDownload = () => {
    downloadTextFile(schemaExportFilename(schema), schemaToExportJson(schema))
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(schemaToExportJson(schema))
      toast.success(t('schemaAgent.header.exportCopied'))
    } catch {
      toast.error(t('schemaAgent.header.exportCopyFailed'))
    }
  }

  return (
    <>
      <DropdownMenuItem onClick={handleDownload}>
        <Download className="mr-2 h-4 w-4" />
        {t('schemaAgent.header.exportDownload')}
      </DropdownMenuItem>
      <DropdownMenuItem onClick={handleCopy}>
        <Copy className="mr-2 h-4 w-4" />
        {t('schemaAgent.header.exportCopy')}
      </DropdownMenuItem>
    </>
  )
}

/**
 * Export action for the schema-detail action cluster (SC-004): serializes the
 * loaded schema to its portable JSON definition and offers it as a file download
 * or a clipboard copy.
 */
export function SchemaExportMenu({ schema }: { schema: Schema }) {
  const { t } = useTranslation()
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 border-border">
          <Download className="mr-1 h-4 w-4" />
          {t('schemaAgent.header.export')}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="border-border bg-card">
        <SchemaExportItems schema={schema} />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** The same choices as a submenu, for a header whose actions have folded into
 * the overflow menu. */
export function SchemaExportSubMenu({ schema }: { schema: Schema }) {
  const { t } = useTranslation()
  return (
    <SchemaHeaderSubMenu icon={Download} label={t('schemaAgent.header.export')}>
      <SchemaExportItems schema={schema} />
    </SchemaHeaderSubMenu>
  )
}
